// TypeScript interfaces for Payments module

// Enums
export enum PaymentStatus {
  Draft = "Draft",
  Submitted = "Submitted",
  Checked = "Checked", 
  Verified = "Verified",
  Approved = "Approved",
  Posted = "Posted",
  Paid = "Paid",
  Rejected = "Rejected",
  Returned = "Returned"
}

export enum PaymentType {
  RA = "RA",
  Retention = "Retention", 
  OnHold = "On-Hold",
  SecurityDeposit = "Security Deposit",
  RetentionRelease = "Retention Release"
}

export enum PaymentMethod {
  NEFT = "NEFT",
  RTGS = "RTGS",
  IMPS = "IMPS",
  Cheque = "Cheque",
  Manual = "Manual"
}

export enum ReleaseType {
  Retention = "Retention",
  OnHold = "On-Hold",
  Mixed = "Mixed"
}

export enum UserRole {
  Maker = "Maker",
  Checker = "Checker",
  Verifier = "Verifier", 
  Approver = "Approver",
  Finance = "Finance"
}

// Core Payment Interfaces
export interface PaymentData {
  id: string;
  paymentVoucherNumber: string;
  paymentDate: string;
  paymentType: PaymentType;
  contractorId: string;
  contractorName: string;
  siteId: string;
  siteName: string;
  projectId?: string;
  projectName?: string;
  legalEntityId: string;
  legalEntityName: string;
  paymentMethod: PaymentMethod;
  bankAccountId: string;
  bankAccountName: string;
  tdsSection?: string;
  tdsRate: number;
  narration?: string;
  status: PaymentStatus;
  
  // Commercial Summary
  sumOfAllocations: number;
  tdsThisPayment: number;
  otherAdjustments: number;
  netPayable: number;
  roundOff?: number;
  
  // Allocations
  allocations: PaymentAllocation[];
  deductions: PaymentDeduction[];
  
  // Finance posting details
  financeDocNumber?: string;
  utrReference?: string;
  postedDate?: string;
  postedBy?: string;
  
  // Audit fields
  createdAt: string;
  createdBy: string;
  submittedAt?: string;
  submittedBy?: string;
  approvedAt?: string;
  approvedBy?: string;
  
  // Flags & Attachments
  hasSoftHold: boolean;
  hasHardHold: boolean;
  attachments: PaymentAttachment[];
  approvalHistory: PaymentApprovalHistoryItem[];
}

export interface PaymentAllocation {
  id: string;
  raBillId?: string;
  raBillNumber?: string;
  workOrderId?: string;
  workOrderNumber?: string;
  raPeriod?: string;
  raBillValue: number;
  alreadyPaid: number;
  billBalance: number;
  thisPaymentAmount: number;
  resultingStatus: "Unpaid" | "Partially Paid" | "Paid";
}

export interface PaymentDeduction {
  id: string;
  deductionType: "TDS" | "Other";
  description: string;
  basis: string;
  amount: number;
  reason?: string;
  attachments?: string[];
}

export interface PaymentAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadDate: string;
  uploadedBy: string;
  url?: string;
}

export interface PaymentApprovalHistoryItem {
  action: string;
  actionBy: string;
  actionDate: string;
  role: UserRole;
  comments?: string;
  status: PaymentStatus;
}

// Approved RA Bills for Payment Creation
export interface ApprovedRaBillData {
  id: string;
  raBillNumber: string;
  workOrderId: string;
  workOrderNumber: string;
  contractorId: string;
  contractorName: string;
  siteId: string;
  siteName: string;
  projectId?: string;
  projectName?: string;
  raPeriod: string;
  raBillValue: number; // Net value
  alreadyPaid: number;
  balance: number;
  status: "Approved" | "Posted";
  approvedDate: string;
  postedDate?: string;
  hasSoftHold: boolean;
  hasHardHold: boolean;
  holdReason?: string;
  contractorPan?: string;
  contractorBankDetails: boolean;
}

// Retention & On-Hold Release
export interface RetentionReleaseData {
  id: string;
  releaseVoucherNumber: string;
  releaseType: ReleaseType;
  paymentDate: string;
  contractorId: string;
  contractorName: string;
  siteId: string;
  siteName: string;
  projectId?: string;
  projectName?: string;
  paymentMethod: PaymentMethod;
  bankAccountId: string;
  bankAccountName: string;
  tdsSection?: string;
  tdsRate: number;
  status: PaymentStatus;
  
  // Commercial Summary
  totalReleaseSelected: number;
  tdsThisRelease: number;
  otherAdjustments: number;
  netPayable: number;
  
  // Release Lines
  releaseLines: ReleaseLineData[];
  deductions: PaymentDeduction[];
  attachments: PaymentAttachment[];
  approvalHistory: PaymentApprovalHistoryItem[];
  
  // Audit
  createdAt: string;
  createdBy: string;
  submittedAt?: string;
  approvedAt?: string;
  postedAt?: string;
  financeDocNumber?: string;
  utrReference?: string;
}

export interface ReleaseLineData {
  id: string;
  type: "Retention" | "On-Hold";
  workOrderId?: string;
  workOrderNumber?: string;
  raBillId?: string;
  raBillNumber?: string;
  scopeReason: string;
  policyStage: string;
  originalAmount: number;
  alreadyReleased: number;
  balance: number;
  thisReleaseAmount: number;
  evidence: PaymentAttachment[];
  
  // Trigger details for retention
  trigger?: "Practical Completion" | "Final Completion" | "DLP Ended";
  completionDate?: string;
  dlpEndDate?: string;
  
  // Hold details for on-hold
  holdReason?: string;
  resolutionDate?: string;
  resolutionMemo?: string;
}

// Filter Interfaces
export interface PaymentFilters {
  paymentDateRange?: [any, any] | null;
  submissionDateRange?: [any, any] | null;
  paymentType?: PaymentType[];
  contractorIds?: string[];
  siteIds?: string[];
  projectIds?: string[];
  statuses?: PaymentStatus[];
  paymentMethods?: PaymentMethod[];
  amountRange?: { min: number; max: number };
  voucherNumber?: string;
  utrReference?: string;
  raBillNumber?: string;
  workOrderNumber?: string;
}

export interface ApprovedRaBillFilters {
  approvedDateRange?: [any, any] | null;
  postedDateRange?: [any, any] | null;
  contractorIds?: string[];
  siteIds?: string[];
  projectIds?: string[];
  paymentStatuses?: string[]; // Unpaid, Partially Paid
  raBillNumber?: string;
  workOrderNumber?: string;
  balanceRange?: { min: number; max: number };
  hasHolds?: boolean;
}

export interface RetentionReleaseFilters {
  releaseType?: ReleaseType[];
  contractorIds?: string[];
  siteIds?: string[];
  projectIds?: string[];
  workOrderNumber?: string;
  raBillNumber?: string;
  milestones?: string[];
  dlpEndDateRange?: [any, any] | null;
  holdReasons?: string[];
  balanceRange?: { min: number; max: number };
}

// Widget/Dashboard Data
export interface PaymentWidgetData {
  totalPayments: number;
  draftCount: number;
  draftPercentage: number;
  submittedCount: number;
  submittedPercentage: number;
  approvedCount: number;
  approvedPercentage: number;
  postedCount: number;
  postedPercentage: number;
  paidCount: number;
  paidPercentage: number;
  totalPaymentValue: number;
  avgPaymentValue: number;
  avgApprovalTime: number;
}

// Component Props
export interface PaymentTableProps {
  data: PaymentData[];
  loading?: boolean;
  onView?: (record: PaymentData) => void;
  onEdit?: (record: PaymentData) => void;
  onDelete?: (record: PaymentData) => void;
  onApprove?: (record: PaymentData) => void;
  onReject?: (record: PaymentData) => void;
}

export interface ApprovedRaBillTableProps {
  data: ApprovedRaBillData[];
  loading?: boolean;
  selectedRows?: string[];
  onRowSelection?: (selectedRowKeys: string[], selectedRows: ApprovedRaBillData[]) => void;
  onCreatePayment?: (record: ApprovedRaBillData) => void;
}

// Master Data
export interface PaymentMasterData {
  contractors: Array<{ label: string; value: string; bankDetailsAvailable: boolean; panAvailable: boolean }>;
  sites: Array<{ label: string; value: string }>;
  projects: Array<{ label: string; value: string; siteId: string }>;
  legalEntities: Array<{ label: string; value: string }>;
  bankAccounts: Array<{ label: string; value: string; accountNumber: string; bankName: string }>;
  tdsSection: Array<{ label: string; value: string; rate: number }>;
  paymentMethods: Array<{ label: string; value: PaymentMethod }>;
  paymentStatuses: Array<{ label: string; value: PaymentStatus }>;
  releaseTypes: Array<{ label: string; value: ReleaseType }>;
}